import * as React from "react";
import { Card, CardContent } from "../../card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../tabs";
import { formatUsd, formatRelativeTime } from "../../format";
import { StatCard } from "../stat-card";
import type { Intent } from "../../tokens";
import type { CustomerPartyDTO } from "../../api/contracts/customers";

/**
 * The canonical customer stage/band → presentation maps (label + intent), the
 * single source of truth shared by the Portfolio table and the Dossier header.
 * The 4 stages map to 4 distinct intents on purpose — `at_risk`/`churned` must
 * NOT collapse to the generic "active" badge (the shared `lifecyclePhase` has no
 * keyword for them, so the Dossier passes these explicitly via
 * `ObjectPage.stateIntent`/`stateLabel` rather than the lifecycle default).
 */
export const CUSTOMER_STAGE_INTENT: Record<CustomerPartyDTO["stage"], Intent> = {
  prospect: "neutral",
  active: "success",
  at_risk: "warning",
  churned: "danger",
};
export const CUSTOMER_STAGE_LABEL: Record<CustomerPartyDTO["stage"], string> = {
  prospect: "Prospect",
  active: "Active",
  at_risk: "At-risk",
  churned: "Churned",
};
export const CUSTOMER_BAND_INTENT: Record<CustomerPartyDTO["health"]["band"], Intent> = {
  healthy: "success",
  watch: "warning",
  at_risk: "danger",
};
export const CUSTOMER_BAND_LABEL: Record<CustomerPartyDTO["health"]["band"], string> = {
  healthy: "Healthy",
  watch: "Watch",
  at_risk: "At-risk",
};

/**
 * CustomerLayers — the Dossier's layers region (docs/customer-module/PRD.md
 * §4.1). A Tabs-atom lens selector (Overview/Sales/Money/Support/Marketing);
 * each lit layer reveals a compact summary of that lens's slice of the
 * customer. Rendered via `ObjectPage.layers` between the header and the
 * cross-layer timeline. In-card panel switching with local state → the Tabs
 * atom (frontend rule 13).
 *
 * v1 reshapes the SUMMARY per lens; coordinating the lens with a feed FILTER
 * (§D5) is a refinement — the timeline below already stamps each row with its
 * layer.
 */

const usd = (minor: number) => formatUsd(minor / 100);

export function CustomerLayers({ party }: { party: CustomerPartyDTO }) {
  const healthValue = party.health.started
    ? `${CUSTOMER_BAND_LABEL[party.health.band]} · ${Math.round(party.health.score * 100)}`
    : "No signal";

  return (
    <Card>
      <CardContent className="flex flex-col gap-4 pt-6">
        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="sales">Sales</TabsTrigger>
            <TabsTrigger value="money">Money</TabsTrigger>
            <TabsTrigger value="support">Support</TabsTrigger>
            <TabsTrigger value="marketing">Marketing</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="pt-4">
            <Grid>
              <StatCard title="Health" value={healthValue} />
              <StatCard title="Lifetime value" value={party.ltv.lifetimeMinor > 0 ? usd(party.ltv.lifetimeMinor) : "—"} />
              <StatCard title="Open deals" value={String(party.openDeals)} />
              <StatCard title="Last touch" value={formatRelativeTime(party.lastTouchAt)} />
            </Grid>
          </TabsContent>

          <TabsContent value="sales" className="pt-4">
            <Grid>
              <StatCard title="Open deals" value={String(party.openDeals)} />
              <StatCard title="Open value" value={party.openDealValueMinor > 0 ? usd(party.openDealValueMinor) : "—"} />
              <StatCard title="Weighted pipeline" value={party.weightedPipelineMinor > 0 ? usd(party.weightedPipelineMinor) : "—"} />
              <StatCard title="Contacts" value={String(party.contactCount)} />
            </Grid>
          </TabsContent>

          <TabsContent value="money" className="pt-4">
            <Grid>
              <StatCard title="Lifetime value" value={party.ltv.lifetimeMinor > 0 ? usd(party.ltv.lifetimeMinor) : "—"} />
              <StatCard title="Last payment" value={formatRelativeTime(party.ltv.lastTxnAt)} />
            </Grid>
          </TabsContent>

          <TabsContent value="support" className="pt-4">
            <Grid>
              <StatCard title="Health" value={healthValue} />
              <StatCard title="Open tickets" value="0 (derived)" description="Modelled in P5" />
            </Grid>
          </TabsContent>

          <TabsContent value="marketing" className="pt-4">
            <Grid>
              <StatCard title="Last touch" value={formatRelativeTime(party.lastTouchAt)} />
            </Grid>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-2 gap-4 @2xl/page:grid-cols-4">{children}</div>;
}
